import { db } from '@/firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Heart, Pill, CalendarClock, ArrowRight } from 'lucide-react';
import { format, addDays, startOfDay } from 'date-fns';
import { useEffect, useState, useMemo } from 'react';

interface ElderCareWidgetProps {
  careProfileIds: string[];
}

interface ProfileData {
  name: string;
  meds: any[];
  appointments: any[];
}

export default function ElderCareWidget({ careProfileIds }: ElderCareWidgetProps) {
  const [profileData, setProfileData] = useState<Record<string, ProfileData>>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const profileIds = useMemo(() => careProfileIds.slice(0, 2), [careProfileIds]);

  // Clear data when profileIds change
  useEffect(() => {
    if (profileIds.length === 0) {
      setProfileData({});
      setLoading(false);
      return;
    }

    // Get date strings
    const now = new Date();
    const todayStr = format(startOfDay(now), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
    const tomorrow = addDays(now, 1);
    const tomorrowStr = format(startOfDay(tomorrow), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");

    // Track subscriptions for cleanup
    const unsubscribers: (() => void)[] = [];

    // For each profile, set up subscriptions
    profileIds.forEach((profileId) => {
      // Initialize empty data for this profile
      setProfileData((prev) => ({
        ...prev,
        [profileId]: { name: '', meds: [], appointments: [] },
      }));

      // Subscribe to profile name
      const profileDocRef = doc(db, 'careProfiles', profileId);
      const unsubscribeProfile = onSnapshot(
        profileDocRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const name = docSnap.data()?.name || 'Unknown';
            setProfileData((prev) => ({
              ...prev,
              [profileId]: { ...prev[profileId], name },
            }));
          }
        },
        (error) => {
          console.error(`Error fetching profile ${profileId}:`, error);
        }
      );

      // Subscribe to active medications
      const medicationsQuery = query(
        collection(db, 'careProfiles', profileId, 'medications'),
        where('isActive', '==', true),
        where('nextDoseAt', '<=', tomorrowStr),
        limit(5),
        orderBy('nextDoseAt')
      );

      const unsubscribeMeds = onSnapshot(
        medicationsQuery,
        (snapshot) => {
          const meds = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setProfileData((prev) => ({
            ...prev,
            [profileId]: { ...prev[profileId], meds },
          }));
        },
        (error) => {
          console.error(`Error fetching medications for ${profileId}:`, error);
        }
      );

      // Subscribe to upcoming appointments
      const appointmentsQuery = query(
        collection(db, 'careProfiles', profileId, 'appointments'),
        where('scheduledAt', '>=', todayStr),
        orderBy('scheduledAt'),
        limit(3)
      );

      const unsubscribeAppts = onSnapshot(
        appointmentsQuery,
        (snapshot) => {
          const appointments = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setProfileData((prev) => ({
            ...prev,
            [profileId]: { ...prev[profileId], appointments },
          }));
        },
        (error) => {
          console.error(`Error fetching appointments for ${profileId}:`, error);
        }
      );

      unsubscribers.push(unsubscribeProfile, unsubscribeMeds, unsubscribeAppts);
    });

    setLoading(false);

    // Cleanup subscriptions
    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [profileIds]);

  // Empty state
  if (profileIds.length === 0) {
    return null;
  }

  // Check if any data loaded
  const isDataLoaded = Object.values(profileData).some(
    (data) => data.name || data.meds.length > 0 || data.appointments.length > 0
  );

  // Format date as "May 6"
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d');
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-6 transition-colors duration-200">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Elder Care</h3>
          <Heart className="w-5 h-5 text-rose-500" fill="currentColor" />
        </div>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Caring for {profileIds.length} {profileIds.length === 1 ? 'person' : 'people'}
        </span>
      </div>

      {/* Loading state */}
      {loading && !isDataLoaded ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rose-500" />
        </div>
      ) : (
        <>
          {/* Medications section */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-3">
              <Pill className="w-4 h-4 text-indigo-500" />
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Medications due</h4>
            </div>

            <div className="space-y-3">
              {Object.entries(profileData).map(([profileId, data]) =>
                data.meds.length > 0 ? (
                  data.meds.map((med) => (
                    <div key={med.id} className="flex items-start space-x-3">
                      <div className="mt-0.5">
                        <Pill className="w-4 h-4 text-rose-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {data.name}: {med.name}
                        </span>
                        {med.dosage && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {med.dosage} • {formatDate(med.nextDoseAt) || 'Today'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : null
              )}

              {Object.values(profileData).every((data) => data.meds.length === 0) && (
                <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                  No meds due today
                </p>
              )}
            </div>
          </div>

          {/* Appointments section */}
          <div>
            <div className="flex items-center space-x-2 mb-3">
              <CalendarClock className="w-4 h-4 text-emerald-500" />
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Upcoming appointments</h4>
            </div>

            <div className="space-y-3">
              {Object.entries(profileData).map(([profileId, data]) =>
                data.appointments.length > 0 ? (
                  data.appointments.map((apt) => (
                    <div key={apt.id} className="flex items-start space-x-3">
                      <div className="mt-0.5">
                        <CalendarClock className="w-4 h-4 text-emerald-400" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                          {data.name}: {apt.title}
                        </span>
                        {apt.scheduledAt && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {formatDate(apt.scheduledAt)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                ) : null
              )}

              {Object.values(profileData).every((data) => data.appointments.length === 0) && (
                <p className="text-sm text-slate-500 dark:text-slate-400 italic">
                  No upcoming appointments
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={() => navigate('/elder-care')}
              className="flex items-center justify-center space-x-2 w-full px-4 py-2 bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 text-sm font-medium text-slate-700 dark:text-slate-300 rounded-lg transition-colors"
            >
              <span>Open Elder Care</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}